import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { WizardProvider } from "./contexts/WizardContext";
import { GoogleOAuthProvider } from "@react-oauth/google";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import ConnectionStatusBanner from "./components/ConnectionStatusBanner";
import { AlertModal } from "./components/ui/alert-modal";
import MaintenancePage from "./components/MaintenancePage";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Support from "./pages/Support";
import RegisterStudent from "./pages/RegisterStudent";
import ChangePassword from "./pages/ChangePassword";
import RegisterParent from "./pages/RegisterParent";
import RegisterTeacher from "./pages/RegisterTeacher";
import ForgotPassword from "./pages/ForgotPassword";
import Contact from "./pages/Contact";
import ContactCoach from "./pages/ContactCoach";
import NotFound from "./pages/NotFound";
import Agenda from "./pages/Agenda";
import SettingsPage from "./pages/Settings";
import TeachersPage from "./pages/Teachers";
import Features from "./pages/Features";
import Pricing from "./pages/Pricing";
import About from "./pages/About";
import Legal from "./pages/Legal";
import MyAccount from "./pages/MyAccount";
import Invoices from "./pages/Invoices";
import InvoicesRecurring from "./pages/InvoicesRecurring";
import InvoiceDetail from "./pages/InvoiceDetail";
import InvoicesBilling from "./pages/InvoicesBilling";
import InvoicesPendingValidation from "./pages/InvoicesPendingValidation";
import InvoicesSchoolBilling from "./pages/InvoicesSchoolBilling";
import InvoicesSchoolPendingValidation from "./pages/InvoicesSchoolPendingValidation";
import InvoiceCreate from "./pages/InvoiceCreate";
import InvoiceEdit from "./pages/InvoiceEdit";
import SchoolInvoiceCreate from "./pages/SchoolInvoiceCreate";
import InvoiceReport from "./pages/InvoiceReport";
import InvoiceSplitCreated from "./pages/InvoiceSplitCreated";
import PaymentPage from "./pages/PaymentPage";
import StudentDetail from "./pages/StudentDetail";
import UpdateBrowser from "./pages/UpdateBrowser";
import Verify from "./pages/Verify";
import SubscriptionPage from "./pages/SubscriptionPage";
import AccountCheck from "./pages/AccountCheck";
import GoogleAuthDone from "./pages/GoogleAuthDone";
import AppleAuthDone from "./pages/AppleAuthDone";
import Shop from "./pages/Shop";
import Messages from "./pages/Messages";
import { updateUserActivity } from "./services/firebaseService";
import RegisterDevice from "@/components/RegisterDevice";
import { useEffect } from "react";
import Chatbot from './components/Chatbot'; 
import InvoiceManualCreate from "./pages/invoiceManualCreate";
import InvoiceTeacherManualCreate from "./pages/InvoiceTeacherManualCreate";
import UserDelete from "./pages/UserDelete";
import { WizardInitializer } from "@/components/WizardInitializer";
import MobileBottomNav from "@/components/MobileBottomNav";
import TwoFactorPrompt from "@/components/security/TwoFactorPrompt";

const queryClient = new QueryClient();
// Important: Replace with your actual Google client ID in production
const GOOGLE_CLIENT_ID =
  "113709107751-i3nl17s0clr60ntunl4b9f9l3i60u7fg.apps.googleusercontent.com";

/**
 * AppContent contient tout ce qui a besoin de useAuth()
 */
const AppContent = () => {
  const { user } = useAuth();
  const location = useLocation();

  /*useEffect(() => {
    if (user && user.userId) {
     updateUserActivity(user.userId);
    }
  }, [location.pathname, user]);*/

  return (
    <>
      <RegisterDevice />
      <WizardInitializer />
      <ScrollToTop />
      <Toaster />
      <Sonner />
      <AlertModal />
      <ConnectionStatusBanner />
      <Routes>
        {/* Public routes that should redirect authenticated users */}
        <Route
          path="/"
          element={
            <ErrorBoundary>
              <Index />
            </ErrorBoundary>
          }
        />
        <Route
          path="/login"
          element={
            <ProtectedRoute redirectAuthenticated={true}>
              <Login />
            </ProtectedRoute>
          }
        />
        <Route
          path="/register"
          element={
            <ProtectedRoute redirectAuthenticated={true}>
              <Register />
            </ProtectedRoute>
          }
        />
        <Route
          path="/register/student"
          element={
            <ProtectedRoute redirectAuthenticated={true}>
              <RegisterStudent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/change-password"
          element={
            <ProtectedRoute redirectAuthenticated={true}>
              <ChangePassword />
            </ProtectedRoute>
          }
        />
        <Route
          path="/register/parent"
          element={
            <ProtectedRoute redirectAuthenticated={true}>
              <RegisterParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/register/teacher"
          element={
            <ProtectedRoute redirectAuthenticated={true}>
              <RegisterTeacher />
            </ProtectedRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <ProtectedRoute redirectAuthenticated={true}>
              <ForgotPassword />
            </ProtectedRoute>
          }
        />

        {/* Public routes that don't need authentication */}
        <Route path="/contact" element={<Contact />} />
        <Route path="/contact-support" 
         element={
            <ProtectedRoute>
              <ContactCoach />
            </ProtectedRoute>
          } />
        <Route path="/features" element={<Features />} />
        <Route path="/pricing" element={<Pricing />} />

        {/* Maintenance routes */}
        <Route path="/documentation" element={<MaintenancePage />} />
        <Route path="/support" element={<MaintenancePage />} />

        <Route path="/about" element={<About />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/my-account/user/delete" 
        element={
          <ProtectedRoute requiredAccountTypes={["C", "S"]}>
            <UserDelete />
          </ProtectedRoute>
        }
        />


        {/* Verification route */}
        <Route
          path="/verify"
          element={
            <ProtectedRoute allowInactive={true}>
              <Verify />
            </ProtectedRoute>
          }
        />

        {/* Account check route */}
        <Route
          path="/account/check"
          element={
            <ProtectedRoute>
              <AccountCheck />
            </ProtectedRoute>
          }
        />

        {/* Payment route */}
        <Route
          path="/payment"
          element={
            <ProtectedRoute>
              <PaymentPage />
            </ProtectedRoute>
          }
        />

        {/* Subscription route */}
        <Route
          path="/subscription"
          element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          }
        />

        {/* Common routes for all authenticated users */}
        <Route
          path="/agenda"
          element={
            <ProtectedRoute>
              <Agenda />
            </ProtectedRoute>
          }
        />

        <Route
          path="/my-account"
          element={
            <ProtectedRoute>
              <MyAccount />
            </ProtectedRoute>
          }
        />

        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <MyAccount />
            </ProtectedRoute>
          }
        />

        <Route
          path="/invoices"
          element={
            <ProtectedRoute>
              <Invoices />
            </ProtectedRoute>
          }
        />

        <Route
          path="/invoices/recurring"
          element={
            <ProtectedRoute>
              <InvoicesRecurring />
            </ProtectedRoute>
          }
        />

        <Route
          path="/messages"
          element={
            <ProtectedRoute>
              <Messages />
            </ProtectedRoute>
          }
        />

        <Route
          path="/shop"
          element={
            <ProtectedRoute>
              <Shop />
            </ProtectedRoute>
          }
        />

        {/* Restricted routes - only for coaches and schools */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute requiredAccountTypes={["C", "S"]}>
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/teachers"
          element={
            <ProtectedRoute requiredAccountTypes={["S"]}>
              <TeachersPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/invoices/billing"
          element={
            <ProtectedRoute
              requiredAccountTypes={["C", "S"]}
              requiresSubscription={true}
            >
              <InvoicesBilling />
            </ProtectedRoute>
          }
        />

        <Route
          path="/School/invoices/billing"
          element={
            <ProtectedRoute
              requiredAccountTypes={["C", "S"]}
              requiresSubscription={true}
            >
              <InvoicesSchoolBilling />
            </ProtectedRoute>
          }
        />

        <Route
          path="/invoices/pending-validation"
          element={
            <ProtectedRoute
              requiredAccountTypes={["C", "S"]}
              requiresSubscription={true}
            >
              <InvoicesPendingValidation />
            </ProtectedRoute>
          }
        />

        <Route
          path="/school/invoices/pending-validation"
          element={
            <ProtectedRoute
              requiredAccountTypes={["S"]}
              requiresSubscription={true}
            >
              <InvoicesSchoolPendingValidation />
            </ProtectedRoute>
          }
        />

        <Route
          path="/invoices/create/:studentId"
          element={
            <ProtectedRoute 
            requiredAccountTypes={["C", "S"]}
            requiresSubscription={true}
            >
              <InvoiceCreate />
            </ProtectedRoute>
          }
        />

        <Route
          path="/school/invoices/create/:studentId"
          element={
            <ProtectedRoute requiredAccountTypes={["C", "S"]}>
              <SchoolInvoiceCreate />
            </ProtectedRoute>
          }
        />

        <Route
          path="/invoices/created"
          element={
            <ProtectedRoute requiredAccountTypes={["C", "S"]}>
              <InvoiceSplitCreated />
            </ProtectedRoute>
          }
        />

        <Route
          path="/invoices/:id"
          element={
            <ProtectedRoute>
              <InvoiceDetail />
            </ProtectedRoute>
          }
        />

        <Route
          path="/invoice/edit/:id"
          element={
            <ProtectedRoute requiredAccountTypes={["C", "S"]}>
              <InvoiceEdit />
            </ProtectedRoute>
          }
        />

        <Route
          path="/invoices/manual-create"
          element={
            <ProtectedRoute requiredAccountTypes={["C", "S"]}>
              <InvoiceManualCreate />
            </ProtectedRoute>
          }
        />

        <Route
          path="/invoices/manual-teacher-create"
          element={
            <ProtectedRoute requiredAccountTypes={["S"]}>
              <InvoiceTeacherManualCreate />
            </ProtectedRoute>
          }
        />

        <Route
          path="/invoices/report"
          element={
            <ProtectedRoute requiredAccountTypes={["C", "S"]}>
              <InvoiceReport />
            </ProtectedRoute>
          }
        />

        <Route
          path="/student/:id"
          element={
            <ProtectedRoute requiredAccountTypes={["C", "S"]}>
              <StudentDetail />
            </ProtectedRoute>
          }
        />

        {/* Browser compatibility route */}
        <Route path="/update-browser" element={<UpdateBrowser />} />

        {/* Auth redirection routes */}
        <Route path="/google-auth-done" element={<GoogleAuthDone />} />
        <Route path="/apple-auth-done" element={<AppleAuthDone />} />

        {/* Catch-all route */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      <MobileBottomNav />
      <TwoFactorPrompt />

      {/* Affiche le Chatbot si l'utilisateur est connecté */}
      {/*user && user.userId && <Chatbot userId={Number(user.userId)} />*/}
    </>
  );
};

/**
 * App contient uniquement les providers globaux
 */
const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <BrowserRouter>
            <LanguageProvider>
              <AuthProvider>
                <WizardProvider>
                  <AppContent />
                </WizardProvider>
              </AuthProvider>
            </LanguageProvider>
          </BrowserRouter>
        </GoogleOAuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
